import type { QueryFunctionContext } from '@tanstack/react-query';
import { request } from '@utilities/graphQLClient';
import { gql } from 'graphql-request';
import type { PrimaryCountryISO } from '@/constants/countries';

export interface Industry {
  label: string;
  slug: string;
  companyCount: number;
  children: string[];
}

export interface IndustryResponse {
  slug: string;
  companyCount: number | null;
  name: string;
  friendlyName: string | null;
  level: number;
  id: string;
  children: Array<{ id: string }>;
}

interface QueryResponse {
  Industries: Array<IndustryResponse>;
}

export const MINIMUM_COMPANIES = 5;

export const GET_INDUSTRY_QUERY_KEY = 'get-industries-mapped';

const createIndustryByLevel = (
  industries: Array<IndustryResponse>,
  level: number,
  filterCompanyCount: boolean
) =>
  industries
    .filter(
      (l) =>
        l.level === level &&
        (filterCompanyCount ? (l.companyCount ?? 0) >= MINIMUM_COMPANIES : true)
    )
    .map((v) => ({
      label: v.friendlyName || v.name,
      companyCount: v.companyCount,
      slug: v.slug,
      children: v.children.map((i) => i.id),
      id: v.id,
    }))
    .reduce((a, { id, ...rest }) => ({ ...a, [id]: rest }), {});

export const getIndustriesByFilterCompanyCount = (
  industries: IndustryResponse[],
  filterCompanyCount: boolean
) => ({
  sector: createIndustryByLevel(industries, 1, filterCompanyCount),
  primary: createIndustryByLevel(industries, 2, filterCompanyCount),
  secondary: createIndustryByLevel(industries, 3, filterCompanyCount),
  tertiary: createIndustryByLevel(industries, 4, filterCompanyCount),
});

export async function fetchIndustries({
  queryKey: [_, country],
}: QueryFunctionContext<[string, PrimaryCountryISO]>) {
  try {
    const response = await request<QueryResponse>(
      gql`
        query industryTree($market: String!) {
          Industries(limit: 300) {
            slug
            companyCount(countryIso2: $market)
            name
            friendlyName
            level
            id
            children {
              id
            }
          }
        }
      `,
      {
        market: country.toUpperCase(),
      }
    );

    return {
      filtered: getIndustriesByFilterCompanyCount(response.Industries, true),
      all: getIndustriesByFilterCompanyCount(response.Industries, false),
    };
  } catch (e) {
    if (e instanceof Error) {
      console.error(e.message);
      throw new Error(e.message);
    }
  }
}
